import { Article } from '@/Article';
import { CLink } from '@/CLink';
import { CommonHero } from '@/CommonHero';
import { ContentDisplay } from '@/ContentDisplay';
import { Faq } from '@/Faq';
import { GradientCta } from '@/GradientCta';
import { ImgArticle } from '@/ImgArticle';
import { Pricing } from '@/Pricing';
import { SingleQ } from '@/SingleQ';
import { PrimaryBtn } from '@/buttons';
import { getNavLink, siteInfo } from '@/layout/NavLinks';
import { Seo } from '@/layout/Seo';
import blueCircles from '@a/images/blue-circles.svg';
import orangeCircles from '@a/images/orange-circles.svg';
import styled from '@emotion/styled';
import { MDXProvider } from '@mdx-js/react';
import useLocationData from '@s/hooks/useLocationData';
import { Link as GatsbyLink, graphql } from 'gatsby';
import { GatsbyImage, IGatsbyImageData, ImageDataLike, getImage } from 'gatsby-plugin-image';
import React, { ReactNode } from 'react';

export const Call = ({ t }: { t?: string }) => (
    <CLink to={siteInfo.phone.link}>{t || siteInfo.phone.text}</CLink>
);

export const MdxLink = ({ t, to }: { t: string; to: string }) => {
    if (!to.startsWith('https') && !to.startsWith('#')) {
        return <GatsbyLink to={getNavLink(to)}>{t}</GatsbyLink>;
    }
    return (
        <a href={to} target={to.startsWith('#') ? '_self' : '_blank'} rel="noreferrer">
            {t}
        </a>
    );
};

const Wrapper = styled.div`
    display: flex;
    position: relative;
    max-width: max-content;
    margin: 32px auto;

    ::before {
        content: url(${orangeCircles});
        transform: translateY(45%);
        display: none;
        margin-right: 16px;
    }

    ::after {
        content: url(${blueCircles});
        display: none;
        margin-left: 16px;
    }

    > .gatsby-image-wrapper {
        border-radius: 15px;
        opacity: 0.9999; //safari overflow bug fix
    }

    @media (min-width: 1280px) {
        margin: 64px auto;

        ::before,
        ::after {
            display: block;
        }
    }
`;

type ServiceProps = {
    data: {
        mdx: {
            frontmatter: {
                heading?: string;
                cover: {
                    og: {
                        gatsbyImageData: IGatsbyImageData;
                    };
                    hero: ImageDataLike;
                };
                topImg: IGatsbyImageData;
                contentImg?: IGatsbyImageData[];
                bigImg?: IGatsbyImageData[];
                articleImg?: IGatsbyImageData[];
            };
        };
    };
    children: ReactNode;
};

export default function ServiceTemplate({ data, children }: ServiceProps) {
    const {
        mdx: {
            frontmatter: { heading, cover, articleImg, topImg, contentImg, bigImg },
        },
    } = data;

    const { title } = useLocationData();

    const topImage = getImage(topImg) as IGatsbyImageData;

    const contentImages = contentImg
        ? contentImg.map(img => getImage(img) as IGatsbyImageData)
        : [];

    const bigImages = bigImg ? bigImg.map(img => getImage(img) as IGatsbyImageData) : [];

    const articleImages = articleImg
        ? articleImg.map(img => getImage(img) as IGatsbyImageData)
        : [];

    // order matters here. its according to content pattern
    const pageImages = [topImage, ...contentImages, ...bigImages, ...articleImages];

    const Img = ({ i, alt }: { i: number; alt?: string }) => (
        <GatsbyImage image={pageImages[i]} alt={alt || title} />
    );

    const BigImg = ({ i, alt }: { i: number; alt?: string }) => (
        <Wrapper>
            <GatsbyImage image={pageImages[i]} alt={alt || title} />
        </Wrapper>
    );

    const shortCodes = {
        ImgArticle,
        Call,
        Link: MdxLink,
        Article,
        Img,
        BigImg,
        Cta: GradientCta,
        Faq,
        SingleQ,
        Pricing,
        PrimaryBtn,
        ContentDisplay,
    };

    return (
        <React.Fragment>
            <CommonHero heading={heading}>
                {cover && (
                    <GatsbyImage
                        image={getImage(cover.hero) as IGatsbyImageData}
                        alt={title}
                        loading="eager"
                    />
                )}
            </CommonHero>
            <MDXProvider components={shortCodes}>
                <Article service>{children}</Article>
            </MDXProvider>
        </React.Fragment>
    );
}

export const query = graphql`
    query ($id: String!) {
        mdx(id: { eq: $id }) {
            frontmatter {
                heading
                cover {
                    og: childImageSharp {
                        gatsbyImageData(width: 1200, height: 630)
                    }
                    hero: childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
                topImg {
                    childImageSharp {
                        gatsbyImageData(width: 1312, height: 408)
                    }
                }
                contentImg {
                    childImageSharp {
                        gatsbyImageData(width: 230, height: 304)
                    }
                }
                bigImg {
                    childImageSharp {
                        gatsbyImageData(width: 1060, height: 480)
                    }
                }
                articleImg {
                    childImageSharp {
                        gatsbyImageData(width: 640, height: 400)
                    }
                }
            }
        }
    }
`;

type HeadProps = {
    location: {
        pathname: string;
    };
} & ServiceProps;

export const Head = ({ location, data }: HeadProps) => {
    return (
        <Seo
            pathname={location.pathname.split('/').slice(-2)[0]}
            image={data.mdx.frontmatter.cover?.og.gatsbyImageData}
        />
    );
};
